/**
 * Serve Mode — the in-car laptop screen (landscape, ≥1100 px wide).
 * Layout follows the deputy's sketch: duty buttons top right; current location
 * and next stop across the top; map + safety alerts + Out of car / In service
 * on the left; the paper list (sortable by % / location / time since last
 * attempt) in the middle; count tiles on the right.
 * Everything except messages, alerts edits and the location label works offline.
 */
import type React from 'react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { addressKey, DEFAULT_SERVE_LAYOUT, distanceFeet, estimateService, localParts, OUTCOME_LABELS, PIN_COLORS, pinState, sideOfTravel, suggestStopOrder, WINDOW_LABELS, type AttemptOutcome, type ServeColumnId, type ServeLayout } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { fmtDate, fmtDateTime } from '../shared/format.ts';
import { useRouter } from '../shared/router.tsx';
import { SlippyMap } from '../shared/SlippyMap.tsx';
import { ErrorBox, useAction } from '../shared/ui.tsx';
import { idb } from './store.ts';
import { useDuty } from './duty.tsx';
import { navigateUrl } from './FieldMap.tsx';
import { SafetyAlerts, unacknowledged } from './alerts.tsx';
import { pinOf, type Pos } from './position.ts';
import { orderFor, useWorkMode, WorkModeBar, WorkModePicker } from './workmode.tsx';
import { ServiceCircles } from './circles.tsx';

interface Row {
  p: any;
  ev: any;
  elig?: { bucket: 'now' | 'later' | 'notToday'; from?: Date; why?: string };
}
type Sort = 'pct' | 'distance' | 'last';
type Filter = 'open' | 'possession' | 'elig_now' | 'elig_later' | 'elig_none' | 'today' | 'return' | 'pickup';

const OPEN = ['ASSIGNED', 'IN_SERVICE'];
const MI = 5280;

function useWhere(pos: Pos | null, online: boolean) {
  const [near, setNear] = useState<{ line1: string; city: string | null; feet: number; source?: 'agency' | 'street'; lat: number; lng: number } | null>(null);
  const last = useRef<{ lat: number; lng: number; at: number; found: boolean } | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 2000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!pos || !online) return;
    // Ask again after moving 100 ft (at most every 4 s), or every 10 s while no address has been found here yet.
    const l = last.current;
    if (l && (Date.now() - l.at < 4000 || (distanceFeet(l, pos) < 100 && (l.found || Date.now() - l.at < 10000)))) return;
    last.current = { lat: pos.lat, lng: pos.lng, at: Date.now(), found: false };
    api.get(`/api/field/where?lat=${pos.lat.toFixed(6)}&lng=${pos.lng.toFixed(6)}`).then((r) => {
      if (last.current) last.current.found = !!r.near;
      // Keep the last address while it's still close (a lookup can be skipped when the street service is busy).
      setNear((prev) => (r.near ? { ...r.near, lat: pos.lat, lng: pos.lng } : prev && distanceFeet(prev, pos) < 1000 ? prev : null));
    }).catch(() => undefined);
  }, [pos?.lat, pos?.lng, online, tick]);
  return near;
}

function useMessages(online: boolean) {
  const [state, setState] = useState<{ messages: any[]; unread: number }>({ messages: [], unread: 0 });
  const load = () => api.get('/api/field/messages').then(setState).catch(() => undefined);
  useEffect(() => {
    if (!online) return;
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [online]);
  return { ...state, load, set: setState };
}

const pct = (n: number | null) => (n == null ? 'NSH' : `${n}%`);
const COL_HEAD: Record<ServeColumnId, string> = { stop: '#', last: 'Last attempt', personal: '% personal', sub: '% sub', mi: 'mi', court: 'Court date', case: 'Case #', type: 'Paper type', attempts: 'Tries' };

export function RouteMode({ rows, pos: rawPos, posErr, online, syncing, syncNote, onSync, onAttempt, header, layout = DEFAULT_SERVE_LAYOUT }: { header: React.ReactNode; rows: Row[]; pos: Pos | null; posErr: string | null; online: boolean; syncing: boolean; syncNote: string | null; onSync: () => void; onAttempt: (paperId: string) => void; layout?: ServeLayout }) {
  const { me } = useAuth();
  const { navigate } = useRouter();
  const { duty, call, error: dutyError } = useDuty();
  // A location worse than ~1.2 mi (desktops without GPS guess from the internet connection) is ignored for distances.
  const pos = rawPos && rawPos.accuracy <= 2000 ? rawPos : null;
  const roughMiles = rawPos && !pos ? Math.round(rawPos.accuracy / 1609) : null;
  const near = useWhere(pos, online);
  const msgs = useMessages(online);
  const [sort, setSort] = useState<Sort>('pct');
  const [filter, setFilter] = useState<Filter>('open');
  const [sel, setSel] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showMsgs, setShowMsgs] = useState(false);
  const [pickups, setPickups] = useState<any[]>([]);
  const [now, setNow] = useState(() => new Date());
  const a = useAction();
  const tz = me?.agency.timezone ?? 'America/New_York';

  useEffect(() => {
    idb.get<any[]>('meta', 'pickups').then((x) => setPickups(x ?? []));
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, [rows]);

  const today = localParts(now, tz).date;
  const pickupIds = new Set<string>(pickups.filter((x) => x.source === 'office').map((x) => x.paper_id));
  const items = useMemo(
    () =>
      rows.map((r) => {
        const pin = pinOf(r.p);
        const last = r.p.attempts.length ? r.p.attempts[r.p.attempts.length - 1] : null;
        return {
          ...r,
          pin,
          est: estimateService(r.p.serveStats, now, tz),
          last,
          milesFromMe: pin && pos ? distanceFeet(pos, pin) / MI : null,
          pickup: pickupIds.has(r.p.id),
          eligibleNow: r.elig ? r.elig.bucket === 'now' : !!r.ev?.attemptNow?.allowed,
          bucket: r.elig?.bucket ?? (r.ev?.attemptNow?.allowed ? 'now' : 'notToday'),
          // Assigned but still at the office: can't be served until picked up.
          open: OPEN.includes(r.p.status) && !pickupIds.has(r.p.id),
          attemptedToday: r.p.attempts.some((x: any) => localParts(new Date(x.attempted_at), tz).date === today),
          alerts: (r.p.safetyAlerts ?? []).length,
        };
      }),
    [rows, now, pos?.lat, pos?.lng, tz, today, pickups],
  );

  // Work mode (Route by likelihood / by location, or Freelance) decides the order when chosen.
  const [wm, setWm] = useWorkMode(me?.user.id, today);
  const [picking, setPicking] = useState(false);
  const orderItems = items.map((x) => ({ id: x.p.id, pin: x.pin, address: x.p.address, est: x.est, open: x.open, eligibleNow: x.eligibleNow }));
  const wmOrder = orderFor(orderItems, wm, pos);
  const wmIndex = wmOrder ? new Map(wmOrder.map((id, i) => [id, i])) : null;

  // Suggested order among papers eligible right now; re-plan when the set changes or I move ~0.1 mi.
  const posKey = pos ? `${pos.lat.toFixed(3)},${pos.lng.toFixed(3)}` : '';
  const planKey = items.filter((x) => x.open && x.eligibleNow && x.pin).map((x) => x.p.id).join('|');
  const greedy = useMemo(
    () =>
      suggestStopOrder(
        items.filter((x) => x.open && x.eligibleNow && x.pin).map((x) => ({ id: x.p.id, lat: x.pin!.lat, lng: x.pin!.lng, addressKey: addressKey(x.p.address), priority: x.ev?.urgency?.tier === 'supervisor_urgent' ? 2 : 0 })),
        pos ? { lat: pos.lat, lng: pos.lng } : null,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [planKey, posKey],
  );
  // With a mode chosen, stops follow that order (eligible papers only; same address = one stop).
  const order = useMemo(() => {
    if (!wmOrder) return greedy;
    const seen = new Map<string, { addressKey: string; lat: number; lng: number; paperIds: string[]; legMiles: number }>();
    for (const id of wmOrder) {
      const x = items.find((i) => i.p.id === id);
      if (!x || !x.eligibleNow || !x.pin) continue;
      const k = addressKey(x.p.address);
      const g = seen.get(k);
      if (g) g.paperIds.push(id);
      else seen.set(k, { addressKey: k, lat: x.pin.lat, lng: x.pin.lng, paperIds: [id], legMiles: 0 });
    }
    return { stops: [...seen.values()], totalMiles: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [greedy, wmOrder?.join('|'), planKey]);
  const stopNo = new Map<string, number>();
  order.stops.forEach((s, i) => s.paperIds.forEach((id) => stopNo.set(id, i + 1)));
  const next = order.stops[0] ? items.find((x) => x.p.id === order.stops[0]!.paperIds[0]) ?? null : null;
  const nextMiles = next?.pin && pos ? distanceFeet(pos, next.pin) / MI : null;
  const side = next?.pin && pos && nextMiles != null && nextMiles <= 0.5 ? sideOfTravel(pos.heading, pos, next.pin) : null;

  const counts = {
    possession: items.filter((x) => x.p.inMyPossession && x.open).length,
    elig_now: items.filter((x) => x.p.inMyPossession && x.open && x.bucket === 'now').length,
    elig_later: items.filter((x) => x.p.inMyPossession && x.open && x.bucket === 'later').length,
    elig_none: items.filter((x) => x.p.inMyPossession && x.open && x.bucket === 'notToday').length,
    today: items.filter((x) => x.attemptedToday).length,
    return: items.filter((x) => !x.open && !x.pickup).length,
    pickup: pickups.length,
  };
  const list = items
    .filter((x) => (filter === 'open' ? x.open : filter === 'possession' ? x.p.inMyPossession && x.open : filter === 'elig_now' ? x.p.inMyPossession && x.open && x.bucket === 'now' : filter === 'elig_later' ? x.p.inMyPossession && x.open && x.bucket === 'later' : filter === 'elig_none' ? x.p.inMyPossession && x.open && x.bucket === 'notToday' : filter === 'today' ? x.attemptedToday : filter === 'return' ? !x.open && !x.pickup : filter === 'pickup' ? x.pickup : false))
    .sort((a, b) => {
      if (filter === 'elig_later') return +(a.elig?.from ?? 0) - +(b.elig?.from ?? 0);
      if (wmIndex) return (wmIndex.get(a.p.id) ?? 1e9) - (wmIndex.get(b.p.id) ?? 1e9);
      if (sort === 'distance') return (a.milesFromMe ?? 1e9) - (b.milesFromMe ?? 1e9);
      if (sort === 'last') return (a.last ? +new Date(a.last.attempted_at) : 0) - (b.last ? +new Date(b.last.attempted_at) : 0);
      return (b.est.personalPct ?? -1) - (a.est.personalPct ?? -1) || (b.est.substitutePct ?? -1) - (a.est.substitutePct ?? -1);
    });

  const selected = items.find((x) => x.p.id === sel) ?? next ?? null;
  const target = selected && selected.open ? selected : null;
  const unacked = target ? unacknowledged(target.p) : [];
  const status = duty?.status ?? 'off';
  const onSceneHere = status === 'on_scene';

  const located = items.filter((x) => x.pin && x.open);
  const map = (
    <SlippyMap
      height="100%"
      fitKey={`${located.length}|${expanded}`}
      ariaLabel="Route map"
      me={pos}
      markers={located.map((x) => ({
        id: x.p.id,
        lat: x.pin!.lat,
        lng: x.pin!.lng,
        color: x.alerts ? '#b3261e' : PIN_COLORS[pinState({ eligibleNow: x.eligibleNow, likelihood: null })],
        label: stopNo.has(x.p.id) ? String(stopNo.get(x.p.id)) : undefined,
        title: `${x.p.civilosNumber} — ${x.p.defendant.full_name}, ${x.p.address.line1}${x.alerts ? ' — SAFETY ALERT' : ''}`,
        diamond: true,
          selected: x.p.id === selected?.p.id,
      }))}
      route={
        order.stops.length
          ? [
              ...(pos ? [pos] : []),
              ...order.stops.map((s) => ({ lat: s.lat, lng: s.lng })),
              ...(wm?.by === 'location' && wm.finish?.lat != null ? [{ lat: wm.finish.lat, lng: wm.finish.lng! }] : []),
            ]
          : undefined
      }
      onMarkerClick={setSel}
    />
  );

  // ── Layout (deputy's own, or agency default): which panels and columns, in what order ──
  const topShown = layout.top.filter((p) => !p.hidden).map((p) => p.id);
  const leftShown = layout.left.filter((p) => !p.hidden).map((p) => p.id);
  const rightShown = layout.right.filter((p) => !p.hidden).map((p) => p.id);
  const cols = layout.columns;
  const whereEl = (
        <div className="rm-where">
          <span className="rm-k">CURRENT LOCATION</span>
          <strong>
            {!pos ? (roughMiles != null ? `Location too rough (±${roughMiles} mi) — this computer has no GPS` : posErr ?? 'Finding your location…') : near ? `${near.source === 'street' ? '' : near.feet < 150 ? '@ ' : 'near '}${near.line1}${near.city ? `, ${near.city}` : ''}` : online ? 'Looking up street address…' : 'Street address shows when back online'}
          </strong>
          {pos && !near && <span className="rm-sub mono">{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</span>}
          {pos && <GpsAge pos={pos} />}
          {!duty?.onDuty && pos && <span className="rm-sub">Off duty — shown on this screen only, not shared with dispatch</span>}
        </div>
  );
  const nextEl = (
        <div className="rm-next" aria-live="polite">
          <span className="rm-k">NEXT STOP</span>
          {next ? (
            <strong>
              {next.p.address.line1}
              {nextMiles != null && <> in {nextMiles < 0.1 ? `${Math.round(nextMiles * MI)} ft` : `${nextMiles.toFixed(1)} mi`}</>}
              {side && side !== 'behind' && <span className={`rm-side ${side}`}>{side === 'ahead' ? 'AHEAD' : `ON ${side.toUpperCase()}`}</span>}
            </strong>
          ) : (
            <strong>{items.some((x) => x.open) ? 'No paper is eligible to attempt right now' : items.some((x) => x.pickup) ? 'Pick up your papers at the office' : 'No papers on this device'}</strong>
          )}
          {next && <span className="rm-sub">{next.p.defendant.full_name} · {order.stops.length} stop(s) eligible now · straight-line distance</span>}
        </div>
  );

  return (
    <div className={`rm ts-${layout.textSize}`}>
      {header}
      {topShown.length > 0 && (
        <div className="rm-strip" style={{ gridTemplateColumns: topShown.length === 1 ? '1fr' : undefined }}>
          {topShown.map((id) => <Fragment key={id}>{id === 'location' ? whereEl : nextEl}</Fragment>)}
        </div>
      )}
      {(dutyError || a.error) && (
        <div className="rm-err">
          {dutyError}
          <ErrorBox error={a.error} />
        </div>
      )}

      <div className={`rm-body${layout.mapSide === 'right' ? ' map-right' : ''}${rightShown.length ? '' : ' no-tiles'}`}>
        {/* Left: map, alerts, out of car / in service */}
        <section className="rm-left">
          {leftShown.map((id) =>
            id === 'map' ? (
              <Fragment key={id}>
          <div className={`rm-map ${expanded ? 'expanded' : ''}`}>
            {map}
            <button className="btn sm rm-expand" onClick={() => setExpanded(!expanded)} aria-label={expanded ? 'Shrink map' : 'Expand map'}>
              {expanded ? '✕ Close map' : '⤢ Expand'}
            </button>
          </div>
              </Fragment>
            ) : id === 'alerts' ? (
              <Fragment key={id}>
          <div className={`rm-alerts ${target?.alerts ? 'hot' : ''}`}>
            <div className="rm-alerts-h">
              <span>ALERTS FOR</span> <strong>{target ? target.p.address.line1 : '—'}</strong>
              {target && (
                <span className="rm-alerts-act">
                  <button className="btn sm" onClick={() => navigate(`/field/paper/${target.p.id}`)}>
                    Open paper
                  </button>
                  <a className="btn sm" href={navigateUrl(target.p)} target="_blank" rel="noreferrer">
                    Navigate
                  </a>
                </span>
              )}
            </div>
            {target ? <SafetyAlerts paper={target.p} online={online} onChanged={onSync} compact /> : <p className="safety-empty">Select a paper.</p>}
          </div>
              </Fragment>
            ) : (
              <Fragment key={id}>
          <div className="rm-bigbtns">
            <button
              className={`rm-big out ${onSceneHere ? 'active' : ''}`}
              disabled={!duty?.onDuty || !target || a.busy || !online || unacked.length > 0}
              title={unacked.length ? 'Read the safety alerts first' : ''}
              onClick={() => a.run(() => call('/api/field/on-scene', { paperId: target!.p.id }))}
            >
              OUT OF CAR
              <small>{onSceneHere ? `since ${fmtDateTime(duty!.statusSince)}${duty?.onScene?.address ? ` · ${duty.onScene.address}` : ''}` : unacked.length ? 'read the alerts first' : target ? `at ${target.p.address.line1}` : 'select a paper'}</small>
            </button>
            <button className="rm-big go" disabled={!target || target.p.onHold || target.p.recalled} onClick={() => onAttempt(target!.p.id)} title="Open the attempt screen even if you can't get within 500 ft (gate, long drive…)">
              ATTEMPT NOW
              <small>{target ? `open attempt screen` : 'select a paper'}</small>
            </button>
            <button className={`rm-big in ${status === 'available' ? 'active' : ''}`} disabled={!duty?.onDuty || status === 'available' || a.busy} onClick={() => a.run(() => call('/api/field/status', { status: 'available' }))}>
              IN SERVICE
              <small>{status === 'available' ? 'in the car · available' : 'back in the car'}</small>
            </button>
          </div>
              </Fragment>
            ),
          )}
        </section>

        {/* Middle: paper list */}
        <section className="rm-list">
          <div className="rm-list-h">
            <h2>{filter === 'open' ? 'Papers to serve' : filter === 'possession' ? 'Papers in my possession' : filter === 'elig_now' ? 'In my possession · eligible now' : filter === 'elig_later' ? 'In my possession · eligible later today' : filter === 'elig_none' ? 'In my possession · not eligible today' : filter === 'today' ? 'Attempted today' : filter === 'return' ? 'To return (completed)' : 'To pick up'}</h2>
            {filter !== 'open' && (
              <button className="btn sm ghost" onClick={() => setFilter('open')}>
                Show all to serve
              </button>
            )}
            {filter !== 'pickup' && !wm && <label className="rm-sort">
              SORT BY{' '}
              <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} aria-label="Sort papers by">
                <option value="pct">% chance of service</option>
                <option value="distance">Location (closest)</option>
                <option value="last">Time since last attempt</option>
              </select>
            </label>}
          </div>
          {filter === 'open' && (picking || !wm) && (
            <WorkModePicker
              today={today}
              items={orderItems}
              pos={pos}
              online={online}
              current={wm}
              onChoose={(w) => (setWm(w), setPicking(false))}
              onCancel={wm ? () => setPicking(false) : undefined}
            />
          )}
          {filter === 'open' && wm && !picking && <WorkModeBar wm={wm} items={orderItems} pos={pos} order={wmOrder} onChange={setWm} onPick={() => setPicking(true)} />}
          {filter === 'pickup' ? (
            <PickupScanner pickups={pickups} items={items} online={online} onDone={onSync} />
          ) : (
            <div className="rm-table-wrap">
              <table className="rm-table">
                <thead>
                  <tr>
                    {cols.includes('stop') && cols.indexOf('stop') === 0 && <th>#</th>}
                    <th>Paper</th>
                    {cols.filter((c) => !(c === 'stop' && cols.indexOf('stop') === 0)).map((c) => (
                      <th key={c} title={c === 'personal' ? `Share of past attempts on this defendant ${WINDOW_LABELS[items[0]?.est.window ?? 'morning']} that ended in personal service` : c === 'sub' ? '…ended in substitute service' : undefined}>
                        {COL_HEAD[c]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 && (
                    <tr>
                      <td colSpan={cols.length + 1} className="muted">
                        Nothing here.
                      </td>
                    </tr>
                  )}
                  {list.map((x) => (
                    <tr key={x.p.id} className={`${x.p.id === selected?.p.id ? 'sel' : ''} ${x.open && !x.eligibleNow ? 'dim' : ''}`} onClick={() => setSel(x.p.id)} onDoubleClick={() => navigate(`/field/paper/${x.p.id}`)}>
                      {cols.includes('stop') && cols.indexOf('stop') === 0 && <td>{stopNo.get(x.p.id) ?? ''}</td>}
                      <td>
                        <div className="rm-p">
                          {x.alerts > 0 && <span className="rm-alert-dot" title="Safety alert">⚠</span>}
                          <strong>{x.p.defendant.full_name}</strong>
                        </div>
                        <div className="rm-p2">
                          {x.p.address.line1}
                          {x.p.address.city ? `, ${x.p.address.city}` : ''} · <span className="mono">{x.p.civilosNumber}</span>
                          {x.open && !x.eligibleNow ? (x.bucket === 'later' && x.elig?.from ? ` · eligible from ${fmtTime(x.elig.from, tz)}` : ' · not eligible today') : ''}
                        </div>
                        {x.open && <ServiceCircles ev={x.ev} compact />}
                      </td>
                      {cols.filter((c) => !(c === 'stop' && cols.indexOf('stop') === 0)).map((c) =>
                        c === 'stop' ? <td key={c}>{stopNo.get(x.p.id) ?? ''}</td>
                        : c === 'last' ? <Fragment key={c}><td className="rm-last">{x.last ? <>{fmtDateTime(x.last.attempted_at)}<br /><span className="muted">{OUTCOME_LABELS[x.last.outcome as AttemptOutcome]}</span></> : <span className="muted">None</span>}</td></Fragment>
                        : c === 'personal' ? <Fragment key={c}><td className={`rm-pct ${x.est.personalPct == null ? 'nsh' : ''}`} title={x.est.basis === 'nsh' ? `Not enough history (${x.est.n} past attempt(s))` : `${x.est.n} past attempts ${x.est.basis === 'window' ? WINDOW_LABELS[x.est.window] : 'at all times'}`}>
                        {pct(x.est.personalPct)}
                      </td></Fragment>
                        : c === 'sub' ? <Fragment key={c}><td className={`rm-pct ${x.est.substitutePct == null ? 'nsh' : ''}`}>{pct(x.est.substitutePct)}</td></Fragment>
                        : c === 'mi' ? <Fragment key={c}><td>{x.milesFromMe == null ? '' : x.milesFromMe.toFixed(1)}</td></Fragment>
                        : c === 'court' ? <td key={c} className="rm-last">{x.p.courtDate ? fmtDate(x.p.courtDate) : <span className="muted">—</span>}</td>
                        : c === 'case' ? <td key={c} className="rm-last mono">{x.p.case?.case_number ?? ''}</td>
                        : c === 'type' ? <td key={c} className="rm-last">{x.p.evaluation?.paperType?.name ?? x.p.paperTypeCode}</td>
                        : <td key={c}>{x.p.attempts.length}</td>,
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {layout.showLegend && <p className="rm-foot">% = share of past attempts on this defendant (all papers) that ended in service, for the current time of day when there's enough history. NSH = not enough service history. Estimates only — rules still decide what's allowed. Double-click a row to open the paper.</p>}
        </section>

        {/* Right: tiles */}
        {rightShown.length > 0 && (
          <section className="rm-tiles">
            {rightShown.map((id) => (
              <Fragment key={id}>
                {id === 'possession' ? (
          <div className="rm-group" role="group" aria-label="Papers in my possession">
            <Tile n={counts.possession} label="Papers in my possession" active={filter === 'possession'} onClick={() => setFilter(filter === 'possession' ? 'open' : 'possession')} />
            <div className="rm-sub">
              <SubTile n={counts.elig_now} label="Eligible now" tone="ok" active={filter === 'elig_now'} onClick={() => setFilter(filter === 'elig_now' ? 'open' : 'elig_now')} />
              <SubTile n={counts.elig_later} label="Later today" tone="warn" active={filter === 'elig_later'} onClick={() => setFilter(filter === 'elig_later' ? 'open' : 'elig_later')} />
              <SubTile n={counts.elig_none} label="Not today" tone="off" active={filter === 'elig_none'} onClick={() => setFilter(filter === 'elig_none' ? 'open' : 'elig_none')} />
            </div>
          </div>
                ) : id === 'today' ? (
<Tile n={counts.today} label="Papers attempted today" active={filter === 'today'} onClick={() => setFilter(filter === 'today' ? 'open' : 'today')} />
                ) : id === 'return' ? (
<Tile n={counts.return} label="Papers to return (completed)" active={filter === 'return'} onClick={() => setFilter(filter === 'return' ? 'open' : 'return')} />
                ) : id === 'pickup' ? (
<Tile n={counts.pickup} label="Papers to pick up" active={filter === 'pickup'} onClick={() => setFilter(filter === 'pickup' ? 'open' : 'pickup')} />
                ) : (
<Tile n={msgs.unread} label="Messages" hot={msgs.unread > 0} active={showMsgs} onClick={() => setShowMsgs(true)} />
                )}
              </Fragment>
            ))}
          </section>
        )}
      </div>
      {showMsgs && <Messages msgs={msgs} online={online} onClose={() => setShowMsgs(false)} />}
    </div>
  );
}

/** One of the three boxes inside "Papers in my possession" — they always add up to that number. */
function SubTile({ n, label, tone, active, onClick }: { n: number; label: string; tone: 'ok' | 'warn' | 'off'; active?: boolean; onClick: () => void }) {
  return (
    <button className={`rm-subtile ${tone} ${active ? 'active' : ''}`} onClick={onClick} aria-pressed={active}>
      <span className="rm-subtile-n">{n}</span>
      <span className="rm-subtile-l">{label}</span>
    </button>
  );
}

const fmtTime = (d: Date, tz: string) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz });

function Tile({ n, label, active, hot, onClick }: { n: number; label: string; active?: boolean; hot?: boolean; onClick: () => void }) {
  return (
    <button className={`rm-tile ${active ? 'active' : ''} ${hot ? 'hot' : ''}`} onClick={onClick} aria-pressed={active}>
      <span className="rm-tile-n">{n}</span>
      <span className="rm-tile-l">{label}</span>
    </button>
  );
}

function Messages({ msgs, online, onClose }: { msgs: ReturnType<typeof useMessages>; online: boolean; onClose: () => void }) {
  const { me } = useAuth();
  const [body, setBody] = useState('');
  const a = useAction();
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (online && msgs.unread) api.post('/api/field/messages/read').then(msgs.set).catch(() => undefined);
  }, []);
  useEffect(() => end.current?.scrollIntoView({ block: 'end' }), [msgs.messages.length]);
  return (
    <div className="rm-msgs" role="dialog" aria-label="Messages with dispatch">
      <div className="rm-msgs-h">
        <h2>Messages · Dispatch</h2>
        <button className="btn sm ghost" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="rm-msgs-list">
        {!online && <p className="muted">Messages need a connection.</p>}
        {online && msgs.messages.length === 0 && <p className="muted">No messages yet.</p>}
        {msgs.messages.map((m) => (
          <div key={m.id} className={`msg ${m.from_user_id === me?.user.id ? 'mine' : 'theirs'}`}>
            <div className="msg-b">{m.body}</div>
            <div className="msg-m">
              {m.from_user_id === me?.user.id ? 'You' : m.from_name} · {fmtDateTime(m.created_at)}
            </div>
          </div>
        ))}
        <div ref={end} />
      </div>
      <form
        className="rm-msgs-f"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            msgs.set(await api.post('/api/field/messages', { body }));
            setBody('');
          });
        }}
      >
        <input aria-label="Message to dispatch" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Message dispatch…" maxLength={2000} disabled={!online} />
        <button className="btn primary" disabled={!body.trim() || a.busy || !online}>
          Send
        </button>
      </form>
      <ErrorBox error={a.error} />
    </div>
  );
}

/**
 * Pick up papers at the office: scan each barcode, then confirm "I have these papers".
 * Confirming moves each paper into the deputy's custody (custody log: scan_to_server by the deputy).
 */
function PickupScanner({ pickups, items, online, onDone }: { pickups: any[]; items: any[]; online: boolean; onDone: () => void }) {
  const { me } = useAuth();
  const [code, setCode] = useState('');
  const [scanned, setScanned] = useState<string[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: string[]; failed: { paperNumber: string; error: string }[] } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const a = useAction();
  const office = pickups.filter((x) => x.source === 'office');
  const transfers = pickups.filter((x) => x.source === 'transfer');
  const norm = (s: string) => s.trim().toUpperCase();
  const expected = new Map(office.map((x) => [norm(x.civilos_number), x]));
  const extras = scanned.filter((n) => !expected.has(n));
  const missing = office.filter((x) => !scanned.includes(norm(x.civilos_number)));
  const who = (n: string) => {
    const x = expected.get(n);
    const it = x && items.find((i) => i.p.id === x.paper_id);
    return it ? `${it.p.defendant.full_name} · ${it.p.address.line1}` : null;
  };
  const add = (raw: string) => {
    const n = norm(raw);
    if (!n) return;
    setResult(null);
    if (scanned.includes(n)) setNote(`${n} already scanned`);
    else {
      setScanned((s) => [...s, n]);
      setNote(expected.has(n) ? `✓ ${n}` : `${n} is not on your pickup list. It will be added to your papers if the office allows it.`);
    }
    setCode('');
  };
  const confirm = () =>
    a.run(async () => {
      const r = await api.post<{ results: { paperNumber: string; ok: boolean; error?: string }[] }>('/api/custody/scan', {
        action: 'issue_to_server',
        serverId: me!.user.id,
        mode: 'grab_a_stack',
        scanned: true,
        paperNumbers: scanned,
      });
      setResult({ ok: r.results.filter((x) => x.ok).map((x) => x.paperNumber), failed: r.results.filter((x) => !x.ok).map((x) => ({ paperNumber: x.paperNumber, error: x.error ?? 'Not accepted' })) });
      setScanned([]);
      setNote(null);
      onDone();
    });

  return (
    <div className="rm-pickup" data-unsaved={scanned.length ? 'true' : undefined} onClick={() => input.current?.focus()}>
      {!online && <div className="alert warn">You’re offline. Pickups are recorded when you’re back online.</div>}
      <form
        className="rm-pickup-scan"
        onSubmit={(e) => {
          e.preventDefault();
          add(code);
        }}
      >
        <input ref={input} autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="Scan each paper’s barcode" aria-label="Scan paper barcode" />
        <button className="btn" disabled={!code.trim()}>
          Add
        </button>
      </form>
      {note && <div className="rm-pickup-note">{note}</div>}
      <div className="rm-pickup-cols">
        <div>
          <div className="rm-k">WAITING FOR YOU ({office.length})</div>
          {office.length === 0 && <div className="muted small">Nothing assigned to you is waiting at the office.</div>}
          <ul className="rm-pickups">
            {office.map((x) => {
              const got = scanned.includes(norm(x.civilos_number));
              return (
                <li key={x.paper_id} className={got ? 'got' : ''}>
                  <span className="rm-check">{got ? '✓' : '○'}</span> <strong className="mono">{x.civilos_number}</strong> <span className="muted small">{who(norm(x.civilos_number))}</span>
                </li>
              );
            })}
          </ul>
          {transfers.length > 0 && (
            <div className="small muted">
              {transfers.length} hand-off{transfers.length === 1 ? '' : 's'} from another deputy: accept under Transfers.
            </div>
          )}
        </div>
        <div>
          <div className="rm-k">SCANNED ({scanned.length})</div>
          {scanned.length === 0 && <div className="muted small">Nothing scanned yet.</div>}
          <ul className="rm-pickups">
            {scanned.map((n) => (
              <li key={n} className={expected.has(n) ? 'got' : 'extra'}>
                <strong className="mono">{n}</strong> {!expected.has(n) && <span className="small">not on your list</span>}{' '}
                <button type="button" className="btn sm ghost" onClick={() => setScanned((s) => s.filter((x) => x !== n))}>
                  remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
      {scanned.length > 0 && (
        <div className="rm-pickup-confirm">
          <div>
            <strong>
              I have these {scanned.length} paper{scanned.length === 1 ? '' : 's'} in hand.
            </strong>
            {missing.length > 0 && (
              <div className="small">
                Not scanned: {missing.map((x) => x.civilos_number).join(', ')}. They stay at the office.
              </div>
            )}
            {extras.length > 0 && <div className="small">{extras.length} not on your list will be checked by the office rules.</div>}
          </div>
          <button className="btn primary big" onClick={confirm} disabled={a.busy || !online}>
            {a.busy ? 'Recording…' : `Confirm — I have ${scanned.length === 1 ? 'this paper' : 'these papers'}`}
          </button>
        </div>
      )}
      {result && (
        <div className={`alert ${result.failed.length ? 'warn' : 'ok'}`}>
          {result.ok.length > 0 && <div>✓ Recorded in the paper log as in your custody: {result.ok.join(', ')}</div>}
          {result.failed.map((f) => (
            <div key={f.paperNumber}>
              ✗ {f.paperNumber}: {f.error}
            </div>
          ))}
        </div>
      )}
      <ErrorBox error={a.error} />
    </div>
  );
}

/** "GPS ±30 ft · 4 s ago" — turns red when the device stops sending fixes, so a frozen location is obvious. */
function GpsAge({ pos }: { pos: Pos }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const secs = Math.max(0, Math.round((Date.now() - pos.at) / 1000));
  const ft = Math.round(pos.accuracy * 3.281);
  const stale = secs > 60;
  const age = secs < 60 ? `${secs} s ago` : `${Math.floor(secs / 60)} min ago`;
  return (
    <span className={`rm-gps${stale || ft > 500 ? ' bad' : ''}`}>
      {pos.source === 'serial' ? `USB GPS (${pos.satellites ?? '?'} sats)` : 'GPS'} ±{ft > 5280 ? `${(ft / 5280).toFixed(1)} mi` : `${ft} ft`} · {age}
      {stale && ' — location not updating; try ☰ → Connect GPS'}
      {!stale && ft > 500 && ' — rough (Wi-Fi) location, not GPS; try ☰ → Connect GPS'}
    </span>
  );
}
